import { Button, TableColumnsType } from 'antd';
import classNames from 'classnames/bind';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ReactComponent as CopyIcon } from '~/assets/icons/copy.svg';
import Permission from '~/components/Common/Permission';
import { FORMAT_DATE_WITH_SECOND } from '~/constants/common';
import PATH from '~/constants/path';
import { usePermissionColumnTable } from '~/hooks/usePermissionColumnTable';
import { Event, RoleName } from '~/models/common';
import { confirmDelete, formatDate } from '~/utils/utils';

import styles from './index.module.scss';

const cx = classNames.bind(styles);

export interface ColumnsProps {
  handleDelete: (id: string) => void;
  isAdmin?: boolean;
}

export const columns = ({
  handleDelete,
  isAdmin,
}: ColumnsProps): TableColumnsType<Event> => {
  const { t } = useTranslation();

  const onDelete = (record: Event): void => {
    confirmDelete((): void => handleDelete(record?.id!));
  };

  return usePermissionColumnTable({
    columns: [
      {
        title: <span>{t('event.list.company-info-label')}</span>,
        key: 'name',
        render: (record: Event): React.ReactNode => {
          return record?.companyId ? (
            <div className={cx('title-wrapper')}>
              <span>{record?.companyName}</span>
              <span className={cx('title-company-id')}>
                {`${t('event.list.company-id-label')}: 
            ${record?.companyId}`}
              </span>
            </div>
          ) : (
            <></>
          );
        },
        width: 200,
        roles: [RoleName.SystemAdmin],
      },
      {
        title: <span>{t('event.list.name-description-label')}</span>,
        key: 'name',
        render: (record: Event): React.ReactNode => {
          return (
            <div className={cx('title-wrapper')}>
              <span className={cx('title-name')}>{record?.name}</span>
              <span className={cx('title-description')}>
                {record?.description}
              </span>
            </div>
          );
        },
      },
      {
        title: <span>{t('event.list.start-date-label')}</span>,
        key: 'startDate',
        render: (record: Event): React.ReactNode => {
          return <>{formatDate(record?.startDate!, FORMAT_DATE_WITH_SECOND)}</>;
        },
        sorter: true,
        width: 200,
      },
      {
        title: <span>{t('event.list.end-date-label')}</span>,
        key: 'endDate',
        render: (record: Event): React.ReactNode => {
          return <>{formatDate(record.endDate!, FORMAT_DATE_WITH_SECOND)}</>;
        },
        sorter: true,
        width: 200,
      },
      {
        title: <span>{t('event.list.created-at-label')}</span>,
        key: 'createdAt',
        render: (record: Event): React.ReactNode => {
          return <>{formatDate(record.createdAt!, FORMAT_DATE_WITH_SECOND)}</>;
        },
        sorter: true,
        width: 200,
      },
      {
        title: <span>{t('event.list.total-user-label')}</span>,
        key: 'eventUsers',
        sorter: true,
        render: (record: Event): React.ReactNode => {
          return (
            <Link
              to={PATH.event[isAdmin ? 'system' : 'company'].user(record?.id!)}
            >
              {t('event.list.total-user-value', {
                value: record?.totalUsers,
              })}
            </Link>
          );
        },
        width: 100,
      },
      {
        title: <span>{t('event.list.action-label')}</span>,
        key: 'action',
        align: 'center',
        render: (record: Event): React.ReactNode => {
          return (
            <div className={cx('btn-wrapper')}>
              <Link
                to={PATH.event[isAdmin ? 'system' : 'company'].edit(
                  record?.id!,
                )}
              >
                <Button type="primary">{t('event.list.btn-edit')}</Button>
              </Link>
              <Link
                to={PATH.event[isAdmin ? 'system' : 'company'].detail(
                  record?.id!,
                )}
              >
                <Button type="primary">{t('event.list.btn-detail')}</Button>
              </Link>
              <Permission
                roles={[
                  RoleName.Company,
                  RoleName.CompanyMember,
                  RoleName.SystemAdmin,
                ]}
              >
                <Button onClick={(): void => onDelete(record)} type="primary">
                  {t('event.list.btn-delete')}
                </Button>
              </Permission>
            </div>
          );
        },
      },
      {
        title: <span>{t('event.list.copy-label')}</span>,
        key: 'copy-action',
        align: 'center',
        render: (record: Event): React.ReactNode => {
          return (
            <div className={cx('btn-wrapper')}>
              <Link
                className={cx('copy-wrapper')}
                to={`${PATH.event.company.create}?copyId=${record?.id}`}
              >
                <CopyIcon />
              </Link>
            </div>
          );
        },
        width: 80,
        roles: [RoleName.Company, RoleName.CompanyMember],
      },
    ],
  });
};
